'use client';

import { useState } from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import { Modal } from '@depop/web-ui-kit/Modal';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { QuizContainer } from '@/app/[locale]/(home)/components/FindYourStyle/QuizContainer';
import { QuizOption } from '@/app/[locale]/(home)/components/FindYourStyle/QuizOption';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
  ModuleInteractionType,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { DEFAULT_QUIZ_ANSWERS } from '@/modules/quiz/constants';
import {
  type CategoryValue,
  type QuizOption as QuizOptionType,
  QuizStep,
} from '@/modules/quiz/types';
import { getOptionsForStep } from '@/modules/quiz/utils';

export function StyleQuizHomepageEntryPoint() {
  const { sendActivityTrackerEvent } = useActivityTracker();
  const options = getOptionsForStep(QuizStep.Category, DEFAULT_QUIZ_ANSWERS);

  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<
    CategoryValue | undefined
  >(undefined);
  const t = useTranslations('quiz');

  function sendActivityTracking(option: QuizOptionType) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.StyleQuiz,
      itemClicked: option.value,
      schemaVersion: '3.0',
    });
  }

  function handleModalClose() {
    setSelectedOption(undefined);
    sendActivityTrackerEvent(
      ActivityTrackerEventType.MODULE_INTERACTION_ACTION,
      {
        interactionType: ModuleInteractionType.ModalClosed,
        moduleName: HomePageModuleNames.StyleQuiz,
      }
    );
  }

  function handleOptionClicked(option: QuizOptionType) {
    sendActivityTracking(option);
    setSelectedOption(option.value as CategoryValue);
    setIsQuizOpen(true);
  }

  return (
    <>
      <section className={styles.quizEntryPoint}>
        <div className={styles.quizContent}>
          <div className={styles.quizDescription}>
            <Heading variant="lg">{t('FindYourStyle.Q1.Title')}</Heading>
            <Body variant="sm-regular">{t('FindYourStyle.Entry.Desc')}</Body>
          </div>
          <div role="radiogroup" className={styles.selectionContainer}>
            {options.map((option) => (
              <QuizOption
                labelPosition="top-left"
                error={false}
                key={option.value}
                option={option}
                selectedValue={selectedOption ?? null}
                onSelect={() => handleOptionClicked(option)}
                label={t(option.labelKey)}
                name="quiz-homepage-entry"
                onHomepage
              />
            ))}
          </div>
        </div>
      </section>
      <Modal
        className={styles.quizModal}
        isOpen={isQuizOpen}
        labelledBy="find-your-style-quiz-title"
        setOpen={setIsQuizOpen}
        onCloseModal={handleModalClose}
        content={() => <QuizContainer initialSelection={selectedOption} />}
      />
    </>
  );
}
